import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';
import { MainPageSection } from './MainPageSection';
import {
  AccountsCarousel,
  AddAccountButton,
} from '@/widgets/accounts-carousel';
import { OVERVIEW_TABS, OverviewTabs, type OverviewTab } from './OverviewTabs';
import { ExchangeSection } from '@/features/exchange';
import { MarketSection } from '@/widgets/market-assets';
import { HideBalanceSection } from '@/shared/components';
import { ErrorText } from '@/shared/components';
import { useOpenAccountAvailability } from '@/features/account-open/model';
import {
  type AccountType,
  useAccountsRests,
  useAccountsStore,
} from '@/entities/account';
import { useAllOperationMethods } from '@/widgets';
import { useAppSettingsStore, useModalStore } from '@/shared/lib';
import { useInternalTransferModals } from '@/features/internal-transfer';
import { useApiTokensModal } from '@/widgets/api-token-list-modal';

const TAB_ACCOUNT_TYPES: Record<OverviewTab, Array<AccountType>> = {
  wallets: ['ewallet'],
  tradingAccounts: ['spot', 'leverage', 'invest', 'earn'],
};

const MainPage = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<OverviewTab>(
    OVERVIEW_TABS.wallets
  );

  const { getState, setState } = useModalStore();
  const { session } = useSessionStore();
  const isUserVerified = session?.additional?.isVerified || false;
  const { isBalancesHidden, setIsBalancesHidden } = useAppSettingsStore();

  const { openInternalTransferFormModal } = useInternalTransferModals();
  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { depositMethods, withdrawMethods } = useAllOperationMethods();
  const navigate = useNavigate();

  const {
    data: accountsRests,
    isLoading: isAccountsRestsLoading,
    error: accountsRestsError,
  } = useAccountsRests();
  const { general } = useAccountsStore();

  const isLoading = isAccountsRestsLoading || !general;

  useOpenAccountAvailability(accountsRests);

  const { openApiTokensModal } = useApiTokensModal();

  useEffect(() => {
    if (getState('openDepositOnMount')) {
      depositMethods.openOperationMethodsModal();
      setState('openDepositOnMount', false);
    }
  }, [depositMethods, getState, setState]);

  if (accountsRestsError) {
    return (
      <ErrorText>
        {accountsRestsError instanceof Error
          ? accountsRestsError.message
          : 'Server error'}
      </ErrorText>
    );
  }

  const handleOpenInternalTransfer = (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction?: 'from' | 'to';
  }) => {
    if (context) {
      openInternalTransferFormModal({
        initialContext: {
          accountType: context.accountType,
          accountNumber: context.accountNumber,
          assetCode: context.assetCode,
          direction: context.direction,
        },
      });
    } else {
      openInternalTransferFormModal();
    }
  };

  const handleAccountClick = (accountNumber: string) => {
    openApiTokensModal(accountNumber);
  };

  const handleExchange = () => {
    if (!isUserVerified) {
      openVerificationInfoModal();
      return;
    }

    navigate('/exchange');
  };

  return (
    <div className="px-[2.4rem] tablet:px-[3.6rem] py-[1.8rem] grow flex flex-col gap-[4.2rem]">
      <div className="flex flex-col gap-[1.6rem]">
        <span className="hero6 flex h-[2.8rem] flex-row items-center gap-[1.2rem]">
          {t('personalAccount.overview.title')}

          <HideBalanceSection
            isBalancesHidden={isBalancesHidden}
            setIsBalancesHidden={setIsBalancesHidden}
            ariaLabel={t(
              `personalAccount.accountsSection.${isBalancesHidden ? 'showBalances' : 'hideBalances'}`
            )}
          />
        </span>

        <div className="flex items-center justify-between gap-[1.6rem]">
          <OverviewTabs value={activeTab} onValueChange={setActiveTab} />

          {activeTab === OVERVIEW_TABS.tradingAccounts && <AddAccountButton />}
        </div>

        <AccountsCarousel
          isBalancesHidden={isBalancesHidden}
          isLoading={isLoading}
          onDeposit={depositMethods.openOperationMethodsModal}
          onWithdraw={withdrawMethods.openOperationMethodsModal}
          onInternalTransfer={handleOpenInternalTransfer}
          accountsRests={
            accountsRests ?? {
              ewallet: [],
              invest: null,
              spot: [],
              leverage: null,
              earn: null,
            }
          }
          onAccountClick={handleAccountClick}
          onExchange={handleExchange}
          visibleTypes={TAB_ACCOUNT_TYPES[activeTab]}
        />
      </div>

      <div className="flex flex-col tablet:flex-row gap-[2.4rem] w-full items-start">
        <MainPageSection
          className="w-full"
          title={t('personalAccount.overview.sections.exchange')}>
          <ExchangeSection />
        </MainPageSection>

        <MainPageSection
          className="w-full"
          title={t('personalAccount.overview.sections.market')}>
          <MarketSection />
        </MainPageSection>
      </div>
    </div>
  );
};

export { MainPage };
